import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { documentService, DocumentType, PDFFormat, PrinterFormat } from '../../services/documentService';

export interface DocumentContextMenuProps {
  documentId: string;
  documentType: DocumentType;
  documentNumber: string;
  customerEmail?: string;
  customerPhone?: string;
  pdfFormat?: PDFFormat;
  printerFormat?: PrinterFormat;
}

export const DocumentContextMenu: React.FC<DocumentContextMenuProps> = ({
  documentId,
  documentType,
  documentNumber,
  customerEmail,
  customerPhone,
  pdfFormat = 'tally_prime',
  printerFormat = 'A4'
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Email dialog state
  const [emailRecipient, setEmailRecipient] = useState(customerEmail || '');
  const [emailSubject, setEmailSubject] = useState(`${documentType.replace('_', ' ')} ${documentNumber}`);
  const [emailMessage, setEmailMessage] = useState(`Please find attached ${documentType.replace('_', ' ')} ${documentNumber}`);
  
  // WhatsApp dialog state
  const [whatsappPhone, setWhatsappPhone] = useState(customerPhone || '');
  const [whatsappMessage, setWhatsappMessage] = useState(`Your ${documentType.replace('_', ' ')} ${documentNumber} is ready`);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePrint = async () => {
    handleClose();
    setLoading(true);
    
    try {
      await documentService.printDocument({
        document_id: documentId,
        document_type: documentType,
        format: pdfFormat,
        printer_format: printerFormat
      });
      
      enqueueSnackbar('Opening print dialog...', { variant: 'info' });
    } catch (error: any) {
      console.error('Print error:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to print document', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    handleClose();
    setLoading(true);
    
    try {
      const filename = `${documentType}_${documentNumber}.pdf`;
      await documentService.saveDocument({
        document_id: documentId,
        document_type: documentType,
        format: pdfFormat,
        printer_format: printerFormat
      }, filename);
      
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Save error:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to download PDF', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailClick = () => {
    handleClose();
    if (!customerEmail) {
      enqueueSnackbar('Customer email not available', { variant: 'warning' });
      return;
    }
    setEmailDialogOpen(true);
  };

  const handleWhatsAppClick = () => {
    handleClose();
    if (!customerPhone) {
      enqueueSnackbar('Customer phone number not available', { variant: 'warning' });
      return;
    }
    setWhatsappDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailRecipient) {
      enqueueSnackbar('Please enter recipient email', { variant: 'warning' });
      return;
    }

    setLoading(true);
    
    try {
      const result = await documentService.sendEmail({
        document_id: documentId,
        document_type: documentType,
        recipient_email: emailRecipient,
        subject: emailSubject,
        message: emailMessage
      });
      
      if (result.success) {
        enqueueSnackbar(`Email sent to ${emailRecipient}`, { variant: 'success' });
        setEmailDialogOpen(false);
      } else {
        enqueueSnackbar(result.message || 'Failed to send email', { variant: 'error' });
      }
    } catch (error: any) {
      console.error('Email error:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to send email', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!whatsappPhone) {
      enqueueSnackbar('Please enter recipient phone number', { variant: 'warning' });
      return;
    }

    setLoading(true);
    
    try {
      const result = await documentService.sendWhatsApp({
        document_id: documentId,
        document_type: documentType,
        recipient_phone: whatsappPhone,
        message: whatsappMessage
      });
      
      if (result.success) {
        enqueueSnackbar(`WhatsApp sent to ${whatsappPhone}`, { variant: 'success' });
        setWhatsappDialogOpen(false);
      } else {
        enqueueSnackbar(result.message || 'Failed to send WhatsApp', { variant: 'error' });
      }
    } catch (error: any) {
      console.error('WhatsApp error:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to send WhatsApp', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        disabled={loading}
        aria-label="document actions"
        data-testid="document-context-menu-button"
      >
        {loading ? <CircularProgress size={24} /> : <MoreVertIcon />}
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handlePrint} data-testid="menu-print">
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleSave} data-testid="menu-save">
          <ListItemIcon>
            <SaveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save PDF</ListItemText>
        </MenuItem>
        
        <MenuItem 
          onClick={handleEmailClick} 
          disabled={!customerEmail}
          data-testid="menu-email"
        >
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Email to Customer</ListItemText>
        </MenuItem>
        
        <MenuItem 
          onClick={handleWhatsAppClick} 
          disabled={!customerPhone}
          data-testid="menu-whatsapp"
        >
          <ListItemIcon>
            <WhatsAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send via WhatsApp</ListItemText>
        </MenuItem>
      </Menu>

      {/* Email Dialog */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Email</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Recipient Email"
              type="email"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              fullWidth
              required
              data-testid="email-recipient-input"
            />
            <TextField
              label="Subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              fullWidth
              required
              data-testid="email-subject-input"
            />
            <TextField
              label="Message"
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              fullWidth
              multiline
              rows={4}
              data-testid="email-message-input"
            />
            <Typography variant="caption" color="text.secondary">
              The PDF will be attached to this email automatically.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            disabled={loading || !emailRecipient}
            data-testid="email-send-button"
          >
            {loading ? <CircularProgress size={24} /> : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog 
        open={whatsappDialogOpen} 
        onClose={() => setWhatsappDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send via WhatsApp</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Recipient Phone"
              type="tel"
              value={whatsappPhone}
              onChange={(e) => setWhatsappPhone(e.target.value)}
              fullWidth
              required
              helperText="Include country code (e.g., +91 for India)"
              data-testid="whatsapp-phone-input"
            />
            <TextField
              label="Message"
              value={whatsappMessage}
              onChange={(e) => setWhatsappMessage(e.target.value)}
              fullWidth
              multiline
              rows={4}
              data-testid="whatsapp-message-input"
            />
            <Typography variant="caption" color="text.secondary">
              The PDF will be sent via WhatsApp Business API.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhatsappDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendWhatsApp} 
            variant="contained" 
            disabled={loading || !whatsappPhone}
            data-testid="whatsapp-send-button"
          >
            {loading ? <CircularProgress size={24} /> : 'Send WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
